<template>
  <div class="progress-container">
    <div class="progress-bar">
      <span class="total-progress" v-if="length !== 1"> {{ calculateTotalProgress }}% </span>
      <div class="inner-bars">
        <div
          v-for="(progress, index) in localProgressData"
          :key="index"
          class="inner-bar"
          :style="{
            width: `${calculateWeightedPercentage(progress)}%`,
            backgroundColor: colors[(length === 1 ? props.index || index : index) % colors.length],
          }"
        >
          <span
            class="progress-label"
            v-if="length === 1"
            :style="{
              right: isLabelNarrow(progress) ? 'auto' : '10px',
              left: isLabelNarrow(progress) ? '10px' : 'auto',
            }"
          >
            {{ calculateWeightedPercentage(progress) }}%
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, toRaw, watch } from 'vue'

  interface ProgressItem {
    initialValue: number
    targetValue: number
    currentValue: number
    weightPercentage: number // Ağırlık alanı eklendi
  }

  const colors = [
    '#007bff',
    '#00cce6',
    '#28a745',
    '#ffc107',
    '#dc3545',
    '#6c757d',
    '#343a40',
    '#6c757d',
    '#343a40',
    '#6c757d',
    '#343a40',
  ]

  const props = defineProps<{
    data: ProgressItem | ProgressItem[] // Tek bir nesne veya dizi alabilir
    index: number // index prop'u tanımlandı
  }>()

  // Tek bir nesne ise diziye çevir
  const localProgressData = ref<ProgressItem[]>([])
  watch(
    () => props.data,
    newData => {
      // Props'u doğrudan değiştirmemek için derin bir kopya oluştur.
      // newData reactive proxy olabilir; structuredClone proxy kopyalayamadığı için önce toRaw ile unwrap ediyoruz.
      const rawData = toRaw(newData)
      localProgressData.value = structuredClone(Array.isArray(rawData) ? rawData : [rawData])
    },
    { immediate: true, deep: true },
  )

  const length = computed(() => {
    return localProgressData.value.length
  })

  const calculateProgress = (progress: ProgressItem) => {
    const { initialValue, targetValue, currentValue } = progress

    if (initialValue === targetValue) {
      return currentValue >= targetValue ? 1 : 0
    }

    // Hedefin başlangıçtan büyük veya küçük olmasına göre hesaplama yap
    const totalDistance = Math.abs(targetValue - initialValue)
    const distanceCovered =
      targetValue > initialValue ? currentValue - initialValue : initialValue - currentValue

    const progressRatio = totalDistance === 0 ? 1 : distanceCovered / totalDistance

    // İlerlemenin 0 ile 1 arasında olduğundan emin ol
    return Math.max(0, Math.min(progressRatio, 1))
  }

  const calculateWeightedPercentage = (progress: ProgressItem) => {
    const totalWeight = localProgressData.value.reduce(
      (sum, item) => sum + item.weightPercentage,
      0,
    )
    if (totalWeight === 0) return 0 // Ağırlık toplamı sıfırsa yüzdelik hesaplama
    const percentage =
      ((calculateProgress(progress) * progress.weightPercentage) /
        (length.value === 1 ? progress.weightPercentage : totalWeight)) *
      100
    return Number.parseFloat(Math.min(Math.max(percentage, 0), 100).toFixed(2)) // Yüzde değerini 0-100 aralığında sınırla
  }

  const calculateTotalProgress = computed(() => {
    const totalWeightedProgress = localProgressData.value.reduce(
      (sum, item) => sum + calculateProgress(item) * item.weightPercentage,
      0,
    )
    const totalWeight = localProgressData.value.reduce(
      (sum, item) => sum + item.weightPercentage,
      0,
    )
    if (totalWeight === 0) return 0
    const percentage = (totalWeightedProgress / totalWeight) * 100
    return Number.parseFloat(Math.min(Math.max(percentage, 0), 100).toFixed(2))
  })

  const isLabelNarrow = (progress: ProgressItem) => {
    const percentage = calculateWeightedPercentage(progress)
    return percentage < 20 // 30px'den küçükse true döner
  }
</script>

<style lang="scss">
  .progress-container {
    @include flexible($align: center);
    width: 100%;
    min-width: 12rem;
    gap: 0.5em;

    .progress-bar {
      flex: 1;
      position: relative;
      height: 20px;
      border-radius: 0.25rem;
      background-color: #e0e0e0; /* Arka plan rengi */
      overflow: hidden;

      .total-progress {
        position: absolute;
        right: 10px;
        z-index: 100; /* Z-index ayarı */
        color: black; /* Yazı rengi */
      }

      .inner-bars {
        display: flex; /* İç barları yan yana yerleştir */
        height: 100%;
        width: 100%; /* Tüm genişliği kapla */
      }

      .inner-bar {
        height: 100%;
        display: flex;
        align-items: center;
        position: relative; /* Pozisyonu relative yaparak içindeki label'ı konumlandır */
        background: linear-gradient(
          135deg,
          rgba(255, 255, 255, 0.1) 25%,
          transparent 25%,
          transparent 50%,
          rgba(255, 255, 255, 0.1) 50%,
          rgba(255, 255, 255, 0.1) 75%,
          transparent 75%,
          transparent
        );
        background-size: 1.25rem 1.25rem; /* Desenin boyutunu ayarlayın */
        background-repeat: repeat; /* Deseni tekrar et */

        .progress-label {
          z-index: 100;
          position: absolute;
          right: 10px; /* Varsayılan konum */
          color: black;
          transition: right 0.2s ease; /* Geçiş efekti */
        }
      }
    }
  }
</style>
